"use client";

import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import Image from "next/image";
import { FileScan, BrainCircuit, ShieldAlert, Sparkles } from "lucide-react";

export default function FeaturesPage() {
  return (
    <div className="flex flex-col min-h-screen bg-white dark:bg-black transition-colors duration-300">
      <Header />
      <main className="flex-1 py-20 px-6">
        <div className="container mx-auto max-w-6xl text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-xs font-bold uppercase tracking-wider mb-6">
            <Sparkles className="w-3 h-3" />
            Core Technology
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold mb-6 text-gray-900 dark:text-white tracking-tight">
            압도적인 채용 효율성,<br className="hidden md:block" />
            <span className="text-blue-600 dark:text-blue-500">기술로 완성하다</span>
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 mb-20 max-w-2xl mx-auto">
            단순 반복 업무는 AI에게 맡기고, 당신은 사람을 보는 데 집중하세요.
          </p>
          
          <div className="grid gap-24 text-left">
            {/* Feature 1: PDF Parsing */}
            <div className="flex flex-col md:flex-row gap-12 items-center">
              <div className="flex-1 space-y-6">
                <div className="w-14 h-14 bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-500 rounded-2xl flex items-center justify-center">
                  <FileScan className="w-7 h-7" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900 dark:text-white">PDF 이력서 초정밀 파싱</h3>
                <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
                  자유 양식의 PDF 이력서도 문제없습니다. OCR과 자연어 처리(NLP) 기술을 결합하여 경력, 스킬, 학력 정보를 구조화된 데이터로 추출합니다.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-gray-700 dark:text-gray-300 font-medium">
                    <div className="w-6 h-6 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-green-600 text-xs">✓</div>
                    포트폴리오 링크 자동 추출
                  </li>
                  <li className="flex items-center gap-3 text-gray-700 dark:text-gray-300 font-medium">
                    <div className="w-6 h-6 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-green-600 text-xs">✓</div>
                    다단 편집 문서 완벽 인식
                  </li>
                </ul>
              </div>
              <div className="flex-1 relative group perspective-1000">
                <div className="absolute inset-0 bg-gradient-to-tr from-orange-500/20 to-pink-500/20 rounded-3xl blur-2xl transform group-hover:scale-105 transition-transform duration-500"></div>
                <div className="relative h-80 w-full rounded-2xl overflow-hidden shadow-2xl border border-gray-200 dark:border-gray-800 transform group-hover:rotate-y-2 transition-transform duration-500 bg-white dark:bg-gray-900">
                  <Image 
                    src="https://images.unsplash.com/photo-1586281380349-632531db7ed4?auto=format&fit=crop&w=1200&q=80"
                    alt="Resume Analysis Dashboard"
                    fill
                    className="object-cover"
                  />
                  {/* Overlay UI Mockup */}
                  <div className="absolute bottom-6 left-6 right-6 bg-white/90 dark:bg-gray-800/90 backdrop-blur p-4 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-600"></div>
                      <div className="h-2 w-24 bg-gray-200 dark:bg-gray-600 rounded"></div>
                    </div>
                    <div className="space-y-2">
                      <div className="h-2 w-full bg-gray-100 dark:bg-gray-700 rounded"></div>
                      <div className="h-2 w-2/3 bg-gray-100 dark:bg-gray-700 rounded"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Feature 2: AI Interview */}
            <div className="flex flex-col md:flex-row-reverse gap-12 items-center">
              <div className="flex-1 space-y-6">
                <div className="w-14 h-14 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-500 rounded-2xl flex items-center justify-center">
                  <BrainCircuit className="w-7 h-7" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900 dark:text-white">맥락을 꿰뚫는 AI 면접관</h3>
                <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
                  "그냥 열심히 했습니다"라는 답변을 걸러냅니다. 지원자의 프로젝트 경험에서 기술적 깊이와 문제 해결 능력을 검증할 수 있는 날카로운 질문을 생성합니다.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-gray-700 dark:text-gray-300 font-medium">
                    <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 text-xs">✓</div>
                    기술 스택별 심층 질문
                  </li>
                  <li className="flex items-center gap-3 text-gray-700 dark:text-gray-300 font-medium">
                    <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 text-xs">✓</div>
                    인성 및 협업 스타일 검증
                  </li>
                </ul>
              </div>
              <div className="flex-1 relative group">
                <div className="absolute inset-0 bg-gradient-to-bl from-blue-500/20 to-cyan-500/20 rounded-3xl blur-2xl transform group-hover:scale-105 transition-transform duration-500"></div>
                <div className="relative h-80 w-full rounded-2xl overflow-hidden shadow-2xl border border-gray-200 dark:border-gray-800 transform group-hover:-rotate-y-2 transition-transform duration-500 bg-white dark:bg-gray-900">
                  <Image 
                    src="https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&w=1200&q=80"
                    alt="AI Interview Interface"
                    fill
                    className="object-cover"
                  />
                  {/* Overlay UI Mockup */}
                  <div className="absolute top-6 right-6 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-bold shadow-lg">
                    AI Question Generated ✨
                  </div>
                </div>
              </div>
            </div>

            {/* Feature 3: Risk Analysis */}
            <div className="flex flex-col md:flex-row gap-12 items-center">
              <div className="flex-1 space-y-6">
                <div className="w-14 h-14 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-500 rounded-2xl flex items-center justify-center">
                  <ShieldAlert className="w-7 h-7" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900 dark:text-white">채용 리스크 사전 차단</h3>
                <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
                  잦은 이직, 경력 부풀리기, 기술 스택 불일치 등 채용 실패로 이어질 수 있는 위험 신호(Red Flags)를 사전에 감지하여 리포트로 제공합니다.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-gray-700 dark:text-gray-300 font-medium">
                    <div className="w-6 h-6 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center text-red-600 text-xs">✓</div>
                    이직 패턴 분석
                  </li>
                  <li className="flex items-center gap-3 text-gray-700 dark:text-gray-300 font-medium">
                    <div className="w-6 h-6 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center text-red-600 text-xs">✓</div>
                    스킬셋 매칭 점수 산출
                  </li>
                </ul>
              </div>
              <div className="flex-1 relative group">
                <div className="absolute inset-0 bg-gradient-to-tr from-red-500/20 to-orange-500/20 rounded-3xl blur-2xl transform group-hover:scale-105 transition-transform duration-500"></div>
                <div className="relative h-80 w-full rounded-2xl overflow-hidden shadow-2xl border border-gray-200 dark:border-gray-800 transform group-hover:rotate-y-2 transition-transform duration-500 bg-white dark:bg-gray-900">
                  <Image 
                    src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1200&q=80"
                    alt="Risk Analysis Report"
                    fill
                    className="object-cover"
                  />
                  <div className="absolute bottom-0 w-full h-1/2 bg-gradient-to-t from-black/80 to-transparent"></div>
                  <div className="absolute bottom-6 left-6 text-white">
                    <div className="text-xs font-bold uppercase text-red-400 mb-1">Risk Alert</div>
                    <div className="text-lg font-bold">Frequent Job Hopping Detected</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
